<template>
  <div class="page-wrap">
    <!-- EXPIRED / NOT FOUND -->
    <v-card v-if="(Info && !Info.active) || notFound" flat class="px-3 py-8">
      <v-container>
        <v-row align="center" justify="center">
          <v-col cols="12" class="text-center">
            <lottie
              :options="defaultOptions"
              v-on:animCreated="handleAnimation"
            />
            <div class="text-h5 font-weight-bold mt-5">
              This link has expired
            </div>
            <div class="text-body-2 grey--text mt-2">
              Please contact customer support to receive a new URL
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- MAIN -->
    <div v-else>
      <!-- FORM -->
      <v-card
        v-if="!cardCollected"
        class="mx-auto overflow-hidden"
        flat
        max-width="620"
      >
        <!-- Header -->
        <v-sheet
          class="header px-4 pt-6 pb-4"
          color="grey lighten-5"
          v-if="Info"
        >
          <div class="text-center">
            <v-avatar
              size="56"
              class="mb-3"
              v-if="Info.Business && Info.Business.imageUrl"
            >
              <v-img :src="Info.Business.imageUrl" alt="Logo" />
            </v-avatar>

            <div
              class="text-h6 font-weight-bold"
              v-if="Info.Business && Info.Business.name"
            >
              {{ Info.Business.name }}
            </div>

            <div
              class="text-body-2 grey--text"
              v-if="Info.Business && Info.Business.phoneNumber"
            >
              {{ Info.Business.phoneNumber }}
            </div>

            <!-- Payment summary pill -->
            <div class="mt-4 d-flex justify-center">
              <div class="pay-pill">
                <span class="mr-2">Payment due</span>
                <strong>{{ paymentLabel }}</strong>
              </div>
            </div>
          </div>
        </v-sheet>

        <!-- Body -->
        <v-card-text class="px-4 pt-5 pb-2">
          <div class="text-subtitle-1 font-weight-bold mb-1">Student Info</div>
          <div class="text-body-2 grey--text mb-4">
            Please enter your details below.
          </div>

          <v-row>
            <v-col cols="12">
              <v-text-field
                outlined
                dense
                autofocus
                v-model="EventData.name"
                label="Full Name"
                :error-messages="nameErrors"
                :hide-details="nameErrors.length === 0"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                outlined
                dense
                v-model="EventData.email"
                label="Email Address"
                @change="fixEmail"
                :error-messages="emailErrors"
                :hide-details="emailErrors.length === 0"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                outlined
                dense
                v-model="EventData.phoneNumber"
                label="Phone Number"
                :error-messages="phoneNumberErrors"
                :hide-details="phoneNumberErrors.length === 0"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                outlined
                dense
                type="date"
                v-model="EventData.dob"
                label="Date of Birth"
                @change="validateAge"
                :error-messages="dobError"
                :hide-details="dobError.length === 0"
              />
              <v-alert v-if="errorMessage" type="error" dense class="mt-2">
                {{ errorMessage }}
              </v-alert>
            </v-col>
          </v-row>

          <!-- Payment section -->
          <div v-if="needsPayment" class="mt-4">
            <v-divider class="my-4" />

            <div class="text-subtitle-1 font-weight-bold mb-1">Payment</div>
            <div class="text-body-2 grey--text mb-3">
              Choose how you would like to pay.
            </div>

            <CreditCard
              v-if="Info"
              :hideBank="true"
              @submit="paymentCollected"
              :autofocus="false"
              :localCollectOnly="true"
              :donationCategoryId="Info.Business.DefaultSchoolFeeId"
              :customer="Info"
              :public="true"
            />
          </div>

          <!-- Free message -->
          <div v-else class="mt-4">
            <v-divider class="my-4" />
            <v-alert type="success" dense>
              This event is free, no payment information is needed.
            </v-alert>
          </div>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="px-4 pb-4">
          <v-btn
            @click="submit"
            :loading="loading"
            :disabled="loading"
            color="primary"
            block
            large
            class="rounded-lg"
          >
            {{ needsPayment ? "Submit & Pay" : "Submit" }}
          </v-btn>
        </v-card-actions>

        <!-- Footer -->
        <v-card-actions class="px-4 pb-6 pt-0">
          <div class="ml-auto text-caption grey--text">
            Powered by <span class="font-weight-medium">taleem.app</span>
            <span v-if="version">, v{{ version }}</span>
          </div>
        </v-card-actions>
      </v-card>

      <!-- SUCCESS -->
      <v-card v-else flat class="mx-auto" max-width="620">
        <v-sheet
          class="header px-4 pt-6 pb-4"
          color="grey lighten-5"
          v-if="Info"
        >
          <div class="text-center">
            <v-avatar
              size="56"
              class="mb-3"
              v-if="Info.Business && Info.Business.imageUrl"
            >
              <v-img :src="Info.Business.imageUrl" alt="Logo" />
            </v-avatar>

            <div class="text-h6 font-weight-bold">{{ Info.Business.name }}</div>
            <div class="text-body-2 grey--text">
              {{ Info.Business.phoneNumber }}
            </div>
          </div>
        </v-sheet>

        <v-card-text class="text-center py-8">
          <lottie
            :height="200"
            :options="defaultOptions"
            name="receiptCheck"
            v-on:animCreated="handleAnimation"
            @onComplete="done"
          />
          <div v-if="showValue" class="mt-2">
            <div class="text-h5 font-weight-bold">All Set</div>
            <div class="text-body-2 grey--text mt-2">
              Your submission has been received.
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-6 pt-0">
          <div class="ml-auto text-caption grey--text">
            Powered by <span class="font-weight-medium">taleem.app</span>
            <span v-if="version">, v{{ version }}</span>
          </div>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
import eventService from "@/modules/Events/service.js";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/1708-success.json";
import CreditCard from "@/modules/PaymentMethod/components/CreditCard.vue";

const pjson = require("../../../../package.json");
const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

export default {
  name: "card-collect",
  components: { CreditCard, Lottie },
  mixins: [validationMixin],

  data() {
    return {
      EventDataId: null,
      EventData: {
        name: null,
        email: null,
        dob: null,
        phoneNumber: null,
        amount: 0,
      },
      age: null,
      errorMessage: "",
      goodToGood: false,
      loading: false,
      notFound: false,
      showValue: false,
      cardCollected: false,
      Info: null,
      complete: false,
      stripe: null,
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: true,
      },
    };
  },

  validations: {
    EventData: {
      name: { required },
      email: { required, email },
      phoneNumber: { required },
      dob: { required },
    },
  },

  computed: {
    needsPayment() {
      const amt = Number(this.EventData.amount || 0);
      return amt > 0;
    },

    paymentLabel() {
      const amt = Number(this.EventData.amount || 0);
      if (amt <= 0) return "Free";
      // keep your existing filter behavior if you already have it globally
      return this.$options.filters?.currency
        ? this.$options.filters.currency(amt)
        : `$${amt.toFixed(2)}`;
    },

    emailErrors() {
      const errors = [];
      if (!this.$v.EventData.email.$dirty) return errors;
      if (!this.$v.EventData.email.required)
        errors.push("Email address is required");
      if (!this.$v.EventData.email.email) errors.push("Email is not valid");
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.EventData.phoneNumber.$dirty) return errors;
      if (!this.$v.EventData.phoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.EventData.name.$dirty) return errors;
      if (!this.$v.EventData.name.required) errors.push("Name is required");
      return errors;
    },
    dobError() {
      const errors = [];
      if (!this.$v.EventData.dob.$dirty) return errors;
      if (!this.$v.EventData.dob.required)
        errors.push("Date of birth is required");
      return errors;
    },
    version() {
      return pjson.version;
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    validateAge() {
      if (!this.EventData.dob) {
        this.errorMessage = "Please enter your date of birth.";
        this.age = null;
        return;
      }

      const today = new Date();
      const dob = new Date(this.EventData.dob);
      const diff = today - dob;
      const age = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));

      this.age = age;

      if (age < 5) {
        this.errorMessage = `Your age is ${age}. You must be at least 5 years old.`;
        this.goodToGood = false;
      } else if (age > 18) {
        this.errorMessage = `Your age is ${age}. You must be under 18 years old.`;
        this.goodToGood = false;
      } else {
        this.goodToGood = true;
        this.errorMessage = "";
      }
    },

    fixEmail() {
      if (!this.EventData.email) return;
      this.EventData.email = this.EventData.email.toLowerCase().trim();
    },

    done() {
      this.showValue = true;
    },

    handleAnimation(anim) {
      this.anim = anim;
    },

    // Keeps your existing payment flow intact
    paymentCollected(event) {
      this.EventData.payment = event;
      this.EventData.business = this.Info.Business;
      this.EventData.EventDataId = this.EventDataId;

      eventService
        .applyEvent(this.EventData)
        .then(() => {
          this.cardCollected = true;
          this.showValue = true;
        })
        .catch((error) => {
          this.loading = false;
          console.log("error adding card2", error);
        });
    },

    // ✅ Updated: if free, submit directly without CC
    submit() {
      this.$v.$touch();
      this.validateAge();

      if (this.$v.$invalid || !this.goodToGood) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return;
      }

      // If payment is required, keep your original flow
      if (this.needsPayment) {
        this.loading = true;
        this.$events.emit("submitCreditCard");
        return;
      }

      // If free, submit without payment
      this.loading = true;

      const payload = {
        ...this.EventData,
        payment: null,
        business: this.Info.Business,
        EventDataId: this.EventDataId,
      };

      eventService
        .applyEvent(payload)
        .then(() => {
          this.cardCollected = true;
          this.showValue = true;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.log("apply free event error", error);
        });
    },

    loadData() {
      const id = this.$route.params.id;

      return eventService
        .getPublicEventById(id)
        .then((response) => {
          if (response.data) {
            this.Info = response.data;
            this.EventDataId = this.Info.id;
            this.EventData.amount = this.Info.amount || 0;
            document.title = this.Info.Business?.name || "Event";
          } else {
            this.notFound = true;
          }
          this.$Progress.finish();
        })
        .catch(() => {
          this.loading = false;
          this.$Progress.finish();
        });
    },
  },
};
</script>

<style scoped>
.page-wrap {
  padding-bottom: 16px;
}

.header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.pay-pill {
  background: rgba(0, 0, 0, 0.05);
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 13px;
}
</style>